import React, { useState, useEffect } from "react";
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Calendar,
  Crown,
  XCircle,
} from "lucide-react";
import {
  openCheckoutSession,
  openCustomerPortal,
} from "../../utils/subscriptionHandler";
import { API_BASE_URL } from "../../config/api";

const SubscriptionSettings = ({ darkMode, user }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [audioUsage, setAudioUsage] = useState(null);

  useEffect(() => {
    checkSubscription();
    checkAudioUsage();
  }, [user]);

  const checkSubscription = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/check-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) throw new Error("Failed to check subscription");

      const data = await response.json();
      setSubscriptionStatus(data);
    } catch (err) {
      console.error("Error checking subscription:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkAudioUsage = async () => {
    if (!user?.id) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/check-audio-limit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) throw new Error("Failed to check audio usage");

      const data = await response.json();
      setAudioUsage(data);
    } catch (err) {
      console.error("Error checking audio usage:", err);
      // Don't set error state, this is not critical
    }
  };

  const handleUpgrade = async () => {
    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) throw new Error("Failed to create checkout session");

      const { url } = await response.json();

      // Open Stripe Checkout (handles web vs mobile)
      await openCheckoutSession(url);

      // Poll for subscription status after 5 seconds
      setTimeout(() => {
        checkSubscription();
      }, 5000);
    } catch (err) {
      console.error("Error creating checkout:", err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleManageBilling = async () => {
    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/create-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) throw new Error("Failed to create portal session");

      const { url } = await response.json();

      // Open Stripe Customer Portal (handles web vs mobile)
      await openCustomerPortal(url);
    } catch (err) {
      console.error("Error opening portal:", err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/cancel-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      if (!response.ok) throw new Error("Failed to cancel subscription");

      // Refresh subscription status
      await checkSubscription();
      setShowCancelConfirm(false);
    } catch (err) {
      console.error("Error canceling subscription:", err);
      setError(err.message);
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  const isSubscribed = subscriptionStatus?.isSubscribed;
  const subscription = subscriptionStatus?.subscription;

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-forest-100">
          Current Plan
        </h3>

        <div
          className={`p-6 rounded-lg border-2 ${
            isSubscribed
              ? "bg-forest-800/40 border-gold-500"
              : "bg-forest-700 border-forest-600"
          }`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {isSubscribed ? (
                <Crown className="w-6 h-6 text-gold-500" />
              ) : (
                <CreditCard className="w-6 h-6 text-forest-300" />
              )}
              <div>
                <h4 className="text-xl font-bold text-forest-100">
                  {isSubscribed ? "Cloud Storage Pro" : "Free Plan"}
                </h4>
                <p className="text-sm text-forest-200">
                  {isSubscribed ? "$10.00/month" : "$0.00/month"}
                </p>
              </div>
            </div>

            {isSubscribed && (
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  subscription?.cancel_at_period_end
                    ? "bg-gold-900/30 text-gold-500 border border-gold-700"
                    : "bg-leaf-900/30 text-leaf-500 border border-leaf-700"
                }`}
              >
                {subscription?.cancel_at_period_end ? "Canceling" : "Active"}
              </span>
            )}
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`w-4 h-4 ${
                  isSubscribed ? "text-leaf-500" : "text-forest-400"
                }`}
              />
              <span className="text-sm text-forest-200">
                {audioUsage
                  ? isSubscribed
                    ? `${audioUsage.current} of ${audioUsage.limit} audio files in cloud storage`
                    : `${audioUsage.current} of ${audioUsage.limit} audio files in cloud storage`
                  : isSubscribed
                    ? "Up to 50 audio files in cloud storage"
                    : "Up to 2 audio files in cloud storage"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`w-4 h-4 ${
                  isSubscribed ? "text-leaf-500" : "text-forest-400"
                }`}
              />
              <span className="text-sm text-forest-200">
                {isSubscribed ? "Sync" : "No sync"} across all devices
              </span>
            </div>
          </div>

          {isSubscribed && subscription && (
            <div className="pt-4 border-t border-forest-600">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-forest-300" />
                <span className="text-forest-200">
                  {subscription.cancel_at_period_end
                    ? `Cancels on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    : `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 rounded-lg flex items-center gap-3 bg-ember-900/30 border border-ember-700">
          <AlertCircle className="w-5 h-5 text-ember-500" />
          <p className="text-sm text-ember-200">{error}</p>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="max-w-md w-full p-6 rounded-lg bg-forest-800">
            <h4 className="text-lg font-semibold mb-2 text-forest-100">
              Cancel Subscription?
            </h4>
            <p className="text-sm mb-4 text-forest-200">
              Your subscription will remain active until the end of your current
              billing period. After that, you'll lose access to Pro features.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={canceling}
                className="flex-1 py-2 px-4 rounded-lg font-medium bg-forest-700 hover:bg-forest-600 text-forest-100"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={canceling}
                className="flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-ember-600 hover:bg-ember-700 text-white"
              >
                {canceling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  "Cancel Subscription"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {!isSubscribed ? (
          <button
            onClick={handleUpgrade}
            disabled={processing}
            className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 ${
              processing
                ? "bg-forest-700 text-forest-400 cursor-not-allowed"
                : "bg-gold-600 hover:bg-gold-700 text-forest-900"
            }`}
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Crown className="w-5 h-5" />
                Upgrade to Pro - $10/month
              </>
            )}
          </button>
        ) : (
          <>
            <button
              onClick={handleManageBilling}
              disabled={processing}
              className="w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-forest-700 hover:bg-forest-600 text-forest-100"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-5 h-5" />
                  Manage Billing
                </>
              )}
            </button>
            {!subscription?.cancel_at_period_end && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={processing || canceling}
                className="w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-transparent border border-ember-700 text-ember-500 hover:bg-ember-900/20"
              >
                <XCircle className="w-5 h-5" />
                Cancel Subscription
              </button>
            )}
          </>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-lg bg-forest-800/60 border border-forest-600">
        <p className="text-sm text-forest-200">
          {isSubscribed
            ? "You can cancel anytime. Your subscription will remain active until the end of your billing period."
            : "Upgrade to unlock unlimited cloud storage and sync across all your devices!"}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSettings;
